import { FC, useEffect, useState } from 'react';
import { Button, Empty, Form, Modal, Input, Row, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { APPOINTMENT_CREATOR } from 'constants/appointments';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import { useAppointmentDetails } from 'providers/AppointmentDetails';
import { createAppointmentNote } from 'services/api/appointments';
import translateIcon from 'assets/icons/translation.svg';

import { CardNote } from 'components/ui';
import { showRestError } from 'utils/showErrorMessage';
import { TranslationModal } from 'components/shared';
import { TranslatableNote } from 'utils/translation';

const { TextArea } = Input;

const Notes: FC = () => {
  // translations
  const { t } = useTranslation();
  // form
  const [form] = Form.useForm();
  // states
  const [isModalVisible, setIsModalVisible] = useState(false);
  // navigation
  const params = useParams();
  // context
  const { toggleModal } = useUIDispatchContext();
  const { appointment, refresh } = useAppointmentDetails();

  const closeNotes = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const onFinish = (values: { note: string }) => {
    createAppointmentNote(params.id as string, values.note)
      .then(() => {
        refresh();
        closeNotes();
      })
      .catch(err => showRestError(err));
  };

  const addButton = (
    <Button onClick={showModal} icon={<PlusOutlined />}>
      {t('appointments_details.add_note')}
    </Button>
  );

  // display copy of the notes; the translate modal pushes translated bodies
  // here (legacy pushed them through the Apollo cache).
  const [notes, setNotes] = useState<TranslatableNote[]>([]);

  useEffect(() => {
    setNotes((appointment?.notes as TranslatableNote[]) || []);
  }, [appointment?.notes]);

  return (
    <>
      {!!notes.length && (
        <Row justify="start" style={{ paddingBottom: 26 }}>
          <Button
            type="primary"
            onClick={() =>
              toggleModal('translation', true, {
                data: notes,
                appointment_id: appointment?.id
              })
            }
          >
            <Image
              src={translateIcon}
              width={20}
              color="white"
              preview={false}
              style={{ paddingRight: 6 }}
            />
            {t('appointments.translate_note')}
          </Button>
        </Row>
      )}
      {notes.length ? (
        notes.map(item => (
          <CardNote
            key={`card-item-${item.id}`}
            title={
              item.sender === APPOINTMENT_CREATOR.doctor
                ? t('appointments.your_message')
                : `${appointment?.patient.first_name} ${appointment?.patient.last_name}`
            }
            sender={item.sender}
            content={item?.note || ''}
            date={dayjs(item?.created_at).format(FULL_DATE_FORMAT)}
          />
        ))
      ) : (
        <Empty>{addButton}</Empty>
      )}
      {!!notes.length && addButton}
      <Modal
        title={t('appointments_details.add_note')}
        open={isModalVisible}
        onOk={form.submit}
        onCancel={closeNotes}
        cancelButtonProps={{ shape: 'round' }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        okButtonProps={{ shape: 'round' }}
      >
        <Form form={form} onFinish={onFinish}>
          <Form.Item name="note" rules={[{ required: true }]}>
            <TextArea placeholder="" />
          </Form.Item>
        </Form>
      </Modal>
      <TranslationModal
        onTranslated={translated =>
          setNotes(prev =>
            prev.map(item => {
              const match = translated.find(x => x.id === item.id);

              return match ? { ...item, note: match.note } : item;
            })
          )
        }
      />
    </>
  );
};

export default Notes;
